import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';
import { isIosApp } from '../utils/platform';

export const PricingSection = () => {
  const navigate = useNavigate();
  const { content: c } = useCountry();
  const [billing, setBilling] = useState('monthly');
  const annual = billing === 'annual';
  const fx = c.fx;
  const localHint = (usd) => (fx ? `${fx.sym}${Math.round(usd * fx.rate)}` : null);
  // Localized price note: non-US visitors see an approximate local amount (charged in USD);
  // US/Global fall back to the existing annual monthly-equivalent note.
  const cardNote = (usdShown, monthlyEquivNote) => {
    const lh = localHint(usdShown);
    if (lh) return `≈ ${lh}${annual ? '/yr' : '/mo'} · billed in USD`;
    return annual ? monthlyEquivNote : null;
  };

  // Pricing (public, SEO) — hidden inside the iOS app (Apple 3.1.1)
  if (isIosApp()) return null;

  return (
    <section id="pricing" className="mb-16" data-testid="pricing-section">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-3">Simple, transparent pricing</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Start <strong>free</strong> with 50 WhatsApp transactions and 3 WhatsApp scans or 10 app scans to try. <strong>Plus</strong> adds unlimited tracking
          and 15 WhatsApp scans or 100 app scans a month; <strong>Pro</strong> adds mileage, 200 WhatsApp scans or 1,500 app scans a month, quarterly tax estimates and the accountant-ready {c.accountant} report.
        </p>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-8" data-testid="pricing-billing-toggle">
        <button
          onClick={() => setBilling('monthly')}
          data-testid="billing-monthly-btn"
          className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${!annual ? 'bg-[#064E3B] text-white' : 'bg-white text-[#064E3B] border border-gray-200'}`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('annual')}
          data-testid="billing-annual-btn"
          className={`px-5 py-2 rounded-full text-sm font-bold transition-colors flex items-center gap-2 ${annual ? 'bg-[#064E3B] text-white' : 'bg-white text-[#064E3B] border border-gray-200'}`}
        >
          Annual
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${annual ? 'bg-[#84CC16] text-black' : 'bg-[#D1FAE5] text-[#064E3B]'}`}>2 months free</span>
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
        <PricingCard
          name="Free"
          price="$0"
          period="forever"
          testid="pricing-free"
          features={['50 transactions / month', '3 free WhatsApp scans or 10 free app scans to try', 'WhatsApp AI expense tracking', 'Auto-categorization', 'Dashboard & monthly summary']}
          cta="Start Free"
          onClick={() => navigate('/auth')}
        />
        <PricingCard
          name="Plus"
          price={annual ? '$49.90' : '$4.99'}
          period={annual ? '/year' : '/month'}
          note={cardNote(annual ? 49.90 : 4.99, '≈ $4.16/mo · billed yearly')}
          testid="pricing-plus"
          features={['Unlimited transactions', '📸 Receipt & PDF scanning (15/mo on WhatsApp · 100/mo in the app)', 'Auto-categorization', 'Monthly summary report', 'CSV export', 'Multi-currency']}
          cta="Get Plus"
          onClick={() => navigate('/auth')}
        />
        <PricingCard
          name="Pro"
          price={annual ? '$199.90' : '$19.99'}
          period={annual ? '/year' : '/month'}
          note={cardNote(annual ? 199.90 : 19.99, '≈ $16.66/mo · billed yearly')}
          popular
          testid="pricing-pro"
          features={['Everything in Plus', `Mileage tracking (${c.mileageRate})`, 'Receipt & PDF scanning (200/mo on WhatsApp · 1,500/mo in the app)', 'Quarterly tax estimates + reminders', `${c.accountant === 'CPA' ? 'CPA-ready' : 'Accountant-ready'} Tax Summary (${c.reportLabel})`, 'PDF & Excel export', 'Professional invoicing (PDF + shareable link)']}
          cta="Get Pro"
          onClick={() => navigate('/auth')}
        />
        <PricingCard
          name="Business"
          price="Let's talk"
          period=""
          note="Custom pricing for teams"
          testid="pricing-business"
          features={['Everything in Pro', 'Professional invoicing (PDF + shareable link)', 'Team roles — up to 5 people (Manager/Accountant/Staff) text expenses on WhatsApp', 'Receipt & PDF scanning (500/mo on WhatsApp · 4,000/mo in the app)', 'Expenses roll into one company account (with "submitted by")', `Unlimited ${c.accountant} exports`, 'Priority support']}
          cta="Contact Us"
          href="mailto:feed@moneyfeed.io?subject=MoneyFeed%20Business%20plan"
        />
      </div>
      <p className="text-center text-sm text-gray-500 mt-6">
        Running a team? <strong className="text-[#064E3B]">Business</strong> lets your whole crew log expenses by text — no software, no extra logins.
      </p>
      <p className="text-center text-sm text-gray-500 mt-6">
        Save with annual billing — <strong className="text-[#064E3B]">2 months free</strong> on every paid plan. Plans auto-renew; cancel anytime and we'll remind you before each charge.
      </p>
      {fx && (
        <p className="text-center text-xs text-gray-400 mt-2" data-testid="pricing-currency-note">
          Local amounts are approximate — all plans are billed in US dollars (USD).
        </p>
      )}
    </section>
  );
};

const PricingCard = ({ name, price, period, features, cta, onClick, href, popular, testid, note }) => (
  <div
    className={`relative rounded-2xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 ${popular ? 'bg-white ring-2 ring-[#84CC16] shadow-2xl' : 'bg-white shadow-lg border border-slate-100'}`}
    data-testid={testid}
  >
    {popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#84CC16] text-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
        MOST POPULAR
      </span>
    )}
    <h3 className="text-lg font-bold text-[#064E3B] mb-2">{name}</h3>
    <div className="flex items-baseline gap-1 mb-1">
      <span className="text-4xl font-bold text-[#064E3B]">{price}</span>
      <span className="text-sm text-gray-500">{period}</span>
    </div>
    <p className="text-xs text-[#65A30D] font-semibold mb-4 h-4" data-testid={`${testid}-note`}>{note || ''}</p>
    <ul className="space-y-2.5 mb-7 flex-1">
      {features.map((f) => (
        <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700">
          <span className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-[#D1FAE5] flex items-center justify-center">
            <Check className="h-3 w-3 text-[#064E3B]" />
          </span>
          {f}
        </li>
      ))}
    </ul>
    {href ? (
      <a href={href} className="w-full">
        <Button className={`w-full font-bold py-5 rounded-xl ${popular ? 'bg-[#84CC16] hover:bg-[#65A30D] text-black' : 'bg-[#064E3B] hover:bg-[#065F46] text-white'}`}>
          {cta}
        </Button>
      </a>
    ) : (
      <Button
        onClick={onClick}
        className={`w-full font-bold py-5 rounded-xl ${popular ? 'bg-[#84CC16] hover:bg-[#65A30D] text-black' : 'bg-[#064E3B] hover:bg-[#065F46] text-white'}`}
      >
        {cta}
      </Button>
    )}
  </div>
);

export default PricingSection;
